<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="open"
        class="fixed inset-0 bg-black/30 backdrop-blur-[1px] z-[60] flex items-center justify-center px-4"
        @click="emit('cancel')"
      >
        <div class="w-full max-w-sm bg-white dark:bg-[#1C2128] rounded-xl shadow-2xl p-5" @click.stop>
          <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Save repeating task</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
            This task repeats. Where should your changes apply?
          </p>

          <div class="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700 overflow-hidden mb-4">
            <div
              v-for="option in SCOPE_OPTIONS"
              :key="option.value"
              class="px-3 py-2.5 transition-colors duration-150"
              :class="scope === option.value ? 'bg-blue-50/70 dark:bg-blue-900/15' : 'hover:bg-gray-50 dark:hover:bg-white/[0.03]'"
            >
              <label class="flex items-start gap-2.5 cursor-pointer">
                <input type="radio" :value="option.value" v-model="scope" class="mt-0.5 text-blue-600 focus:ring-blue-500" />
                <span class="min-w-0">
                  <span class="block text-[13px] font-medium text-gray-800 dark:text-gray-100">{{ option.label }}</span>
                  <span class="block text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{{ option.hint }}</span>
                </span>
              </label>
            </div>
          </div>

          <!-- Says plainly which fields travel and which never do. Without
               this the two non default options look like they would also
               copy this occurrence's status and due date across the series,
               which is exactly the thing nobody wants and the backend
               refuses to do. -->
          <p v-if="scope !== 'only'" class="flex items-start gap-2 px-3 py-2.5 mb-4 rounded-lg bg-gray-50 dark:bg-white/[0.04] text-xs leading-relaxed text-gray-500 dark:text-gray-400">
            <svg class="w-3.5 h-3.5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"/>
            </svg>
            <span>Title, description, color, and priority are shared. Status, due date, and reminder always stay on this task only.</span>
          </p>

          <div class="flex justify-end gap-2">
            <button
              type="button"
              @click="emit('cancel')"
              class="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >Cancel</button>
            <button
              type="button"
              @click="emit('confirm', scope)"
              :disabled="saving"
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 hover:shadow-md active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100 text-white text-sm font-semibold rounded-lg transition-all duration-150"
            >{{ saving ? 'Saving...' : 'Save' }}</button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
// Shown when saving one occurrence of a repeating task, so an edit meant for
// the whole series doesn't silently land on a single day (and vice versa).
// Mirrors TaskDeleteDialog's three-way prompt on purpose: the same three
// scopes, worded the same way, so delete and save behave alike.
import { ref, watch } from 'vue'

const SCOPE_OPTIONS = [
  { value: 'only', label: 'This task only', hint: 'Other days in the series stay as they are.' },
  { value: 'following', label: 'This and all future tasks', hint: 'Past occurrences keep what they say now.' },
  { value: 'all', label: 'All tasks in the series', hint: 'Every occurrence, past and future, is updated.' },
]

const props = defineProps({
  open: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
})
const emit = defineEmits(['confirm', 'cancel'])

// "This task only" is the default because it is the one choice that can't
// damage anything the user didn't intend.
const scope = ref('only')
watch(() => props.open, (open) => { if (open) scope.value = 'only' })
</script>

<style scoped>
.fade-enter-active { transition: opacity 0.15s ease; }
.fade-leave-active { transition: opacity 0.12s ease; }
.fade-enter-from,
.fade-leave-to     { opacity: 0; }
</style>
